import Checkbox, { CheckboxNoFunc } from '@components/Checkbox';
import { Collapse } from '@components/Collapse';
import Tip from '@components/Tip';
import Title from '@components/Title';
import { Validator } from '@components/Validator';
import ViewSlider, { type ViewSliderKey } from '@components/ViewSlider';
import useToggle from '@hooks/useToggle';
import useTourEditing from '@hooks/useTourEditing';
import { $sceneMapApi } from '@stores/dataStores';
import { MainPlayer } from '@stores/krpPlayerStores';
import { type Hotspot, type SceneData, SceneType, SelectStatus } from '@types';
import getSelectedStatus from '@utils/getSelectedStatus';
import limitNum from '@utils/limitNum';
import { roundNum } from '@utils/roundNum';
// @ts-ignore
import { nanoid } from 'nanoid';
import { useEffect, useState } from 'react';
import PanelBase from '../../panel/PanelBase';
import { onPlayerViewChange } from '../../player/actions/viewActions';
import { onHotspotClick } from '../actions';
import ItemIcon from './ItemIcon';
import getFeatName from './getFeatName';

interface Props {
  scene: SceneData;
  index: number;
  onRequestClose: () => void;
}

export function PanelLayerSetting({ scene, index, onRequestClose }: Props) {
  const hotspotList = scene.hotspot ?? [];
  const configLayer = scene.config.layers ?? {};
  const layerData = (configLayer.list ?? [])[index];
  const isMatrix = scene.type === SceneType.matrix;

  const { state, setState, isChanged } = useTourEditing(layerData, {
    id: `hl_${nanoid()}`,
    fovMin: 0.1,
    fovMax: 180,
    hotspotIds: [],
  });
  const { fovMax, fovMin, hotspotIds } = state;
  const digits = isMatrix ? 2 : 1;

  const [fovInfo, setFovInfo] = useState({ range: '', initial: '' });
  const [curFov, setCurFov] = useState(fovMin);
  const [sliderRange, setSliderRange] = useState([0.1, 180]);

  const loading = useToggle(true);

  useEffect(() => {
    const view = MainPlayer.get()?.getView();
    if (!view) return;
    const round = roundNum(digits);
    setFovInfo({
      range: `${round(view.fovmin)}-${round(view.fovmax)}`,
      initial: isMatrix ? '' : `${round(scene.view.fov)}`,
    });
    MainPlayer.get()?.lookTo({
      fov: fovMin,
      hlookat: view.hlookat,
      vlookat: view.vlookat,
    });
    if (isMatrix) {
      setState((draft) => {
        draft.fovMin = round(
          layerData?.fovMin
            ? limitNum({
                min: view.fovmin,
                max: view.fovmax,
                value: layerData.fovMin,
              })
            : view.fovmin
        );
        draft.fovMax = round(
          layerData?.fovMax
            ? limitNum({
                min: view.fovmin,
                max: view.fovmax,
                value: layerData.fovMax,
              })
            : view.fovmax
        );
      });
      setSliderRange([view.fovmin, view.fovmax]);
    }
    const unwatch = onPlayerViewChange.watch(({ fov, fovmax, fovmin }) => {
      setCurFov(round(limitNum({ min: fovmin, max: fovmax, value: fov })));
    });
    loading.off();
    return () => {
      unwatch();
    };
  }, []);

  useEffect(() => {
    return onHotspotClick.watch(({ id }) => {
      handleItemToggle(id);
    });
  }, [hotspotIds]);

  const handleConfirm = () => {
    $sceneMapApi.updateScene({
      id: scene.id,
      updater: (draft) => {
        draft.config.layers ??= {};
        draft.config.layers.list ??= [];
        draft.config.layers.list[index] = state;
      },
    });
    onRequestClose();
  };

  const handleDelete = () => {
    $sceneMapApi.updateScene({
      id: scene.id,
      updater: (draft) => {
        draft.config.layers ??= {};
        draft.config.layers.list ??= [];
        draft.config.layers.list.splice(index, 1);
      },
    });
  };

  const handleSelectAll = (status: SelectStatus) => {
    setState((draft) => {
      if (status === SelectStatus.all) {
        draft.hotspotIds = [];
      } else {
        draft.hotspotIds = hotspotList.map((x) => x.id);
      }
    });
  };

  const handleItemToggle = (id: string) => {
    setState((draft) => {
      if (hotspotIds.includes(id)) {
        draft.hotspotIds = draft.hotspotIds.filter((_id) => _id !== id);
      } else {
        draft.hotspotIds.push(id);
      }
    });
  };

  const handleFovChange = (params: { key: ViewSliderKey; value: number }) => {
    const dataKey = (
      {
        left: 'fovMin',
        center: 'fov',
        right: 'fovMax',
      } as const
    )[params.key];
    const view = MainPlayer.get()?.getView()!;
    MainPlayer.get()?.lookTo({
      fov: params.value,
      hlookat: view.hlookat,
      vlookat: view.vlookat,
      duration: 0,
    });
    if (dataKey === 'fovMin' || dataKey === 'fovMax') {
      setState((draft) => {
        draft[dataKey] = roundNum(digits, params.value);
      });
    }
  };

  const renderItem = (item: Hotspot) => {
    const isOn = hotspotIds.includes(item.id);
    return (
      <div
        key={item.id}
        className="mb-10 flex h-35 w-full cursor-pointer items-center rounded bg-black-#34 px-10"
        onClick={() => handleItemToggle(item.id)}
      >
        <CheckboxNoFunc className="mr-5 shrink-0" checked={isOn} />
        <ItemIcon icon={item.icon} />
        <div className="mx-10 truncate text-base text-white">{item.title}</div>
        <div className="ml-auto flex-shrink-0 text-sm text-black-gray">
          {getFeatName(item.data.code)}
        </div>
      </div>
    );
  };

  return (
    <PanelBase
      isChanged={isChanged}
      title={<Title>显示层级设置</Title>}
      onRequestClose={onRequestClose}
      onDeleteClick={handleDelete}
      onConfirm={handleConfirm}
    >
      {!loading.isOn && (
        <>
          <Collapse title="当前场景视角（FOV）信息">
            <Tip>{`视角（FOV）范围：${fovInfo.range}`}</Tip>
            {!!fovInfo.initial && (
              <Tip>{`初始视角（FOV）：${fovInfo.initial}`}</Tip>
            )}
          </Collapse>
          <Collapse title="设置热点显示层级范围">
            <ViewSlider
              min={sliderRange[0]}
              max={sliderRange[1]}
              step={isMatrix ? 0.01 : 0.1}
              leftValue={fovMin}
              centerValue={curFov}
              rightValue={fovMax}
              leftTitle="最近"
              rightTitle="最远"
              centerTitle="当前视角"
              digits={digits}
              centerReadonly
              onChange={handleFovChange}
            />
          </Collapse>
          <Collapse title="选择要显示的热点">
            <Tip className="mb-10 text-orange">
              注：选择的热点仅在当前层级下显示，未选择的热点则始终显示
            </Tip>

            <div className="mb-10 flex h-35 w-full cursor-pointer items-center rounded bg-black-#34 px-10">
              <Checkbox
                label="全选"
                status={getSelectedStatus(
                  hotspotIds.length,
                  hotspotList.length
                )}
                onClick={handleSelectAll}
              />
            </div>
            <Validator
              value={hotspotIds}
              validFn={(value) => !!value.length}
              warningMessage="请勾选要显示的热点"
            >
              {() => hotspotList.map(renderItem)}
            </Validator>
          </Collapse>
        </>
      )}
    </PanelBase>
  );
}
